import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Layout, User, Zap, Sun, Moon, LogIn, LogOut, FlaskConical, Trophy, Book, FileText, Wifi, WifiOff } from 'lucide-react';
import { TierBadge } from './TierBadge';
import { MembershipTier } from '../types/membership';
import { useUserProfile } from '../hooks/useUserProfile';
import CookieSettings from './CookieSettings';
import { SyncStatus } from '../services/syncService';

interface NavbarProps {
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
  isAuthenticated: boolean;
  onLogout: () => void;
  membershipTier?: MembershipTier;
  syncStatus?: SyncStatus;
}

// Map pathname to nav item ID for active highlighting
const pathnameToNavId: Record<string, string> = {
  '/': 'landing',
  '/why': 'why',
  '/signup': 'signup',
  '/research': 'research',
  '/stories': 'stories',
  '/profile': 'profile',
  '/achievements': 'achievements',
  '/login': 'login',
  '/library': 'library',
};

const Navbar: React.FC<NavbarProps> = ({ theme, onToggleTheme, isAuthenticated, onLogout, membershipTier, syncStatus }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showCookieSettings, setShowCookieSettings] = useState(false);

  // Get membership tier from useUserProfile hook - only use as fallback if prop not provided
  const { membershipProfile } = useUserProfile();

  // Use prop (from context) as primary source, fall back to hook value, then default to 'guest'
  const displayTier = membershipTier || 'guest';

  // Determine active nav item from current pathname
  const activeNavId = pathnameToNavId[location.pathname] || '';

  // Base items visible to everyone
  const baseNavItems = [
    { id: 'why', label: 'Why', icon: FlaskConical, path: '/why' },
    { id: 'signup', label: 'Pricing', icon: Zap, path: '/signup' },
    { id: 'research', label: 'Research', icon: FileText, path: '/research' },
    { id: 'stories', label: 'Stories', icon: Book, path: '/stories' },
  ];

  // Items for all users (including unauthenticated)
  const allUsersNavItems = [
    { id: 'profile', label: 'Profile', icon: User, path: '/profile' },
  ];

  // Show Profile to all users, not just authenticated
  const visibleItems = [...baseNavItems, ...allUsersNavItems];

  // Add achievements link for all users (to encourage upgrades)
  const achievementsItem = [{ id: 'achievements', label: 'Achievements', icon: Trophy, path: '/achievements' }];

  // All visible nav items (leaderboards removed - feature no longer planned)
  const allNavItems = [...visibleItems, ...achievementsItem];

  return (
    <>
    <nav className="fixed top-0 w-full bg-gradient-to-br from-slate-100 to-blue-50 dark:from-slate-900/80 dark:to-slate-800/80 backdrop-blur-md border-b border-primary/30 dark:border-primary/20 shadow-sm sm:shadow-md z-50 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
            <Zap className="h-8 w-8 text-primary drop-shadow-lg" />
            <span className="ml-2 text-xl font-bold text-slate-900 dark:text-white tracking-tight drop-shadow-sm whitespace-nowrap">Flash-Read</span>
          </div>
          <div className="flex items-center space-x-2 md:space-x-4">
            <div className="hidden lg:flex space-x-1">
              {allNavItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => navigate(item.path)}
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors border-b-2 border-transparent ${
                    activeNavId === item.id
                      ? 'text-primary bg-blue-50 dark:bg-blue-900/30 shadow-sm border-primary'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'
                  }`}
                >
                  <item.icon className="h-4 w-4 mr-1.5" />
                  {item.label}
                </button>
              ))}
            </div>

            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 mx-2 hidden lg:block"></div>

            {!isAuthenticated && (
              <button
                onClick={() => navigate('/login')}
                className="hidden lg:flex items-center px-3 py-2 rounded-md text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                <LogIn className="h-4 w-4 mr-1.5" />
                <span className="hidden sm:inline">Login</span>
              </button>
            )}

            <button
              onClick={onToggleTheme}
              className="w-11 h-11 flex items-center justify-center rounded-full text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors focus:outline-none shadow-sm"
              aria-label="Toggle Dark Mode"
            >
              {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
            </button>

            {isAuthenticated && (
               <TierBadge tier={displayTier} size="sm" />
             )}

             {/* Sync status indicator for Ultra users */}
             {isAuthenticated && displayTier === 'ultra' && syncStatus && (
               <div
                 className="hidden md:flex items-center justify-center w-8 h-8 rounded-full cursor-help"
                 title={`Sync status: ${syncStatus}`}
                 style={{
                   backgroundColor:
                     syncStatus === 'synced' ? '#10b981' :
                     syncStatus === 'syncing' ? '#3b82f6' :
                     syncStatus === 'pending' ? '#f59e0b' :
                     syncStatus === 'offline' ? '#6b7280' :
                     '#ef4444'
                 }}
               >
                 {syncStatus === 'synced' ? <Wifi className="h-4 w-4 text-white" /> :
                  syncStatus === 'syncing' ? <Wifi className="h-4 w-4 text-white animate-pulse" /> :
                  syncStatus === 'offline' ? <WifiOff className="h-4 w-4 text-white" /> :
                  <Wifi className="h-4 w-4 text-white" />}
               </div>
             )}

            {isAuthenticated && (
               <button
                 onClick={onLogout}
                 className="hidden lg:flex items-center px-3 py-2 rounded-md text-sm font-medium text-slate-500 dark:text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400 transition-colors"
               >
                 <LogOut className="h-4 w-4 mr-1.5" />
                 <span className="hidden sm:inline">Logout</span>
               </button>
            )}
          </div>
        </div>
      </div>

      {/* Icon Nav Bar - visible below lg */}
      <div className="lg:hidden border-t border-slate-200 dark:border-slate-800 flex justify-around p-2 bg-gradient-to-br from-slate-100 to-blue-50 dark:from-slate-900/80 dark:to-slate-800/80">
       {visibleItems.map((item) => (
           <button
             key={item.id}
             onClick={() => navigate(item.path)}
             className={`w-11 h-11 flex items-center justify-center rounded-md ${
               activeNavId === item.id ? 'text-primary drop-shadow-md' : 'text-slate-500 dark:text-slate-400'
             }`}
             aria-label={item.label}
           >
             <item.icon className="h-6 w-6" />
           </button>
       ))}
       {!isAuthenticated && (
          <button
            onClick={() => navigate('/login')}
            className={`w-11 h-11 flex items-center justify-center rounded-md ${
              activeNavId === 'login' ? 'text-primary drop-shadow-md' : 'text-slate-500 dark:text-slate-400'
            }`}
            aria-label="Login"
          >
            <LogIn className="h-6 w-6" />
          </button>
       )}
       {isAuthenticated && (
          <button
            onClick={onLogout}
            className="w-11 h-11 flex items-center justify-center rounded-md text-slate-500 dark:text-slate-400 hover:text-red-500 drop-shadow-md"
            aria-label="Logout"
          >
            <LogOut className="h-6 w-6" />
          </button>
       )}
      </div>
    </nav>

    {/* Cookie Settings Modal */}
    {showCookieSettings && (
      <CookieSettings
        showAsModal={true}
        onClose={() => setShowCookieSettings(false)}
      />
    )}
    </>
  );
};

export default Navbar;
